import { Dialog, useMediaQuery } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useAppDispatch, useAppSelector } from '../../../helpers/store';
import { selectMandalListByGender } from '../../Contacts/contacts.ducks';
import YogAdminFilters from './YogAdminFilters';
import YogAdminResults from './YogAdminResults';
import { ConfirmMarkAsIncompleteModal } from './components/Modal/ConfirmMarkAsIncompleteModal';
import { ConfirmRejectModal } from './components/Modal/ConfirmRejectModal';
import { ConfirmRemoveHoldModal } from './components/Modal/ConfirmRemoveHoldModal';
import { ViewRegistrationModal } from './components/Modal/ViewRegistrationModal';
import { REGISTRATION_STATUS } from './constants/RegistrationStatus';
import { useRegistrationOptions } from './useRegistrationOptions';
import {
  IFilters,
  InitialActiveFilterValues,
  IRegistration,
  resetSelectedRegistration,
  RespState,
  searchRegistrations,
  setActiveFilters,
  setSelectedRegistration,
  updateRegistration,
  updateRegistrationsState
} from './yogAdmin.duck';
import { useManageRegistrationFeature } from 'src/hooks/useYogAdminFeature';

interface IYogAdminProps {
  eventId: string;
}

const YogAdmin = ({ eventId }: IYogAdminProps) => {
  const { classes, theme } = useStyles();
  const dispatch = useAppDispatch();
  const yogAdmin = useAppSelector((state) => state.admin.yogAdmin);
  const mandalList = useAppSelector(selectMandalListByGender);
  const { isSuperAdmin } = useAppSelector((state) => state.userAccess);
  const {
    paginationMeta,
    registrations,
    selectedRegistration,
    activeFilters,
    getRegistrationsApiState,
    updateRegistrationApiState
  } = yogAdmin;

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [showConfirmRejectModal, setShowConfirmRejectModal] = useState<IRegistration | null>(null);
  const [showConfirmRemoveHoldModal, setShowConfirmRemoveHoldModal] =
    useState<IRegistration | null>(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showConfirmMarkAsIncompleteModal, setShowConfirmMarkAsIncompleteModal] =
    useState<IRegistration | null>(null);

  useEffect(() => {
    dispatch(searchRegistrations({ pageNumber: 1, eventId: eventId }));
  }, [eventId]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const manageRegistrationFeature = useManageRegistrationFeature({ eventId });
  const allowedActions = manageRegistrationFeature?.allowedActions || [];
  const hideQuickActions = allowedActions.length === 0;

  const isSearchLoading = getRegistrationsApiState === RespState.PENDING;
  const isUpdatingRegistration = updateRegistrationApiState === RespState.PENDING;

  const { paymentRules } = useRegistrationOptions(eventId);

  const onFilterSubmit = (values: IFilters) => {
    setPageNumber(1);
    dispatch(setActiveFilters(values));
    dispatch(searchRegistrations({ pageNumber: 1, eventId: eventId }));
  };

  const fetchMore = useCallback(() => {
    if (!isSearchLoading) {
      setPageNumber(pageNumber + 1);
      dispatch(searchRegistrations({ pageNumber: pageNumber + 1, eventId: eventId }));
    }
  }, [paginationMeta.maxPageNumber, pageNumber]);

  const handleApproveRegistration = (registration: IRegistration) => {
    if (!hideQuickActions) {
      dispatch(
        updateRegistration({
          ...registration,
          status: REGISTRATION_STATUS.APPROVED
        })
      );
    }
  };

  const handlePaidRegistration = (registration: IRegistration, paid: boolean) => {
    if (!hideQuickActions) {
      dispatch(
        updateRegistration({
          ...registration,
          status: paid ? REGISTRATION_STATUS.PAID_OVERRIDE : REGISTRATION_STATUS.APPROVED
        })
      );
    }
  };

  const openConfirmRejectModal = (registration: IRegistration) => {
    if (!hideQuickActions) {
      setShowConfirmRejectModal(registration);
    }
  };

  const openConfirmRemoveHoldModal = (registration: IRegistration) => {
    setShowConfirmRemoveHoldModal(registration);
  };

  const handleRejectRegistration = (registration: IRegistration) => {
    if (!hideQuickActions) {
      dispatch(
        updateRegistration({
          ...registration,
          status: REGISTRATION_STATUS.REJECTED
        })
      );
      //Add async then after fixing ReduxJS Toolkit
      setShowConfirmRejectModal(null);
    }
  };

  const handleUpdateRegistration = (registration: IRegistration) => {
    if (!hideQuickActions) {
      dispatch(updateRegistration(registration));
      dispatch(updateRegistrationsState(registration));
    }
  };

  const handleClickFamilyId = (familyId: string) => {
    onFilterSubmit({ ...activeFilters, familyId });
  };

  const handleClearFilterByField = (field: keyof IFilters) => {
    onFilterSubmit({ ...activeFilters, [field]: InitialActiveFilterValues[field] });
  };

  const handleClearAllFilters = () => {
    dispatch(setActiveFilters(InitialActiveFilterValues));
    dispatch(searchRegistrations({ pageNumber: 1, eventId: eventId }));
  };

  const handleToggleFilterModal = () => {
    setShowFilterModal(!showFilterModal);
  };

  const openConfirmMarkAsIncompleteModal = (registration: IRegistration) => {
    setShowConfirmMarkAsIncompleteModal(registration);
  };

  const handleMarkAsIncompleteRegistration = (registration: IRegistration) => {
    if (!hideQuickActions) {
      dispatch(updateRegistration({ ...registration, status: REGISTRATION_STATUS.INCOMPLETE }));
    }
    setShowConfirmMarkAsIncompleteModal(null);
  };

  return (
    <div className={classes.container}>
      {!isMobile ? (
        <YogAdminFilters
          eventId={eventId}
          isSuperAdmin={isSuperAdmin}
          isLoading={isSearchLoading}
          maxPageNumbers={paginationMeta.maxPageNumber}
          isError={getRegistrationsApiState === RespState.FAILED}
          handleClearAllFilters={handleClearAllFilters}
          onFilterSubmit={onFilterSubmit}
          mandalOptions={mandalList}
          activeFilters={activeFilters}
        />
      ) : null}
      <YogAdminResults
        registrations={registrations}
        totalResults={paginationMeta.total}
        fetchMore={fetchMore}
        onClickRegistration={(registration) => {
          dispatch(setSelectedRegistration(registration));
        }}
        openConfirmRejectModal={openConfirmRejectModal}
        openConfirmRemoveHoldModal={openConfirmRemoveHoldModal}
        approveRegistration={handleApproveRegistration}
        rejectRegistration={handleRejectRegistration}
        onClickFamilyId={handleClickFamilyId}
        handleClearFilterByField={handleClearFilterByField}
        activeFilters={activeFilters}
        isUpdatingRegistration={isUpdatingRegistration}
        isMobile={isMobile}
        toggleFilterModal={handleToggleFilterModal}
        paymentRules={paymentRules}
        hideQuickActions={hideQuickActions}
      />
      <Dialog
        fullScreen={isMobile}
        open={!!selectedRegistration}
        PaperProps={{
          classes: {
            root: classes.dialogContainer
          }
        }}
        onClose={() => dispatch(resetSelectedRegistration())}
      >
        {selectedRegistration ? (
          <ViewRegistrationModal
            cancelCallBack={() => {
              dispatch(resetSelectedRegistration());
            }}
            updateRegistration={handleUpdateRegistration}
            approveRegistration={handleApproveRegistration}
            rejectRegistration={openConfirmRejectModal}
            handlePaidRegistration={handlePaidRegistration}
            openConfirmRemoveHoldModal={openConfirmRemoveHoldModal}
            openConfirmMarkAsIncompleteModal={openConfirmMarkAsIncompleteModal}
            isUpdatingRegistration={isUpdatingRegistration}
            hideActions={hideQuickActions}
            eventId={eventId}
          />
        ) : null}
      </Dialog>
      <Dialog
        fullScreen={false}
        open={!!showConfirmRejectModal}
        PaperProps={{
          classes: {
            root: classes.dialogContainer
          }
        }}
      >
        {showConfirmRejectModal ? (
          <ConfirmRejectModal
            cancelCallBack={() => {
              setShowConfirmRejectModal(null);
            }}
            confirmCallBack={handleRejectRegistration}
            registration={showConfirmRejectModal}
            isUpdatingRegistration={isUpdatingRegistration}
          />
        ) : null}
      </Dialog>
      <Dialog
        fullScreen={false}
        open={!!showConfirmRemoveHoldModal}
        PaperProps={{
          classes: {
            root: classes.dialogContainer
          }
        }}
      >
        {showConfirmRemoveHoldModal ? (
          <ConfirmRemoveHoldModal
            cancelCallBack={() => {
              setShowConfirmRemoveHoldModal(null);
            }}
            confirmCallBack={(registration) => {
              setShowConfirmRemoveHoldModal(null);
              handleApproveRegistration(registration);
            }}
            registration={showConfirmRemoveHoldModal}
            isUpdatingRegistration={isUpdatingRegistration}
          />
        ) : null}
      </Dialog>
      <Dialog
        fullScreen={isMobile}
        open={showFilterModal}
        PaperProps={{
          classes: {
            root: classes.dialogContainer
          }
        }}
        onClose={handleToggleFilterModal}
      >
        <YogAdminFilters
          isSuperAdmin={isSuperAdmin}
          isLoading={isSearchLoading}
          maxPageNumbers={paginationMeta.maxPageNumber}
          isError={getRegistrationsApiState === RespState.FAILED}
          handleClearAllFilters={handleClearAllFilters}
          onFilterSubmit={onFilterSubmit}
          mandalOptions={mandalList}
          activeFilters={activeFilters}
          onCancel={handleToggleFilterModal}
          eventId={eventId}
        />
      </Dialog>
      <Dialog
        fullScreen={isMobile}
        open={!!showConfirmMarkAsIncompleteModal}
        PaperProps={{
          classes: {
            root: classes.dialogContainer
          }
        }}
      >
        {showConfirmMarkAsIncompleteModal ? (
          <ConfirmMarkAsIncompleteModal
            cancelCallBack={() => {
              setShowConfirmMarkAsIncompleteModal(null);
            }}
            confirmCallBack={handleMarkAsIncompleteRegistration}
            registration={showConfirmMarkAsIncompleteModal}
            isUpdatingRegistration={isUpdatingRegistration}
          />
        ) : null}
      </Dialog>
    </div>
  );
};

const useStyles = makeStyles()(() => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    background: 'white'
  },
  dialogContainer: {
    width: '400px'
  }
}));

export default YogAdmin;
